/* =========================================================
   CREA TU PACK · TwoFive
   Configurador de packs (ES module, no dependencies).
   ========================================================= */

const STORAGE_KEY = 'twofive_promo2x_state';
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

class CreaTuPack {
  constructor(root) {
    this.root = root;
    this.maxSizes = [2, 3, 4];
    this.discountByCount = {
      2: parseFloat(root.dataset.discount2 || '5'),
      3: parseFloat(root.dataset.discount3 || '10'),
      4: parseFloat(root.dataset.discount4 || '15'),
    };
    this.moneyFormat = root.dataset.moneyFormat || '€{{amount}}';
    // Universal promo formula:
    //   discount = sum(N cheapest prices) × (pct / 100)
    //   total    = subtotal − discount
    // discountedQty = how many of the cheapest items get the discount.
    this.promoDiscountPct  = parseFloat(root.dataset.promoDiscountPct  || '50');
    this.promoDiscountedQty = parseInt (root.dataset.promoDiscountedQty || '1', 10);
    this.texts = {
      totalLabel:        root.dataset.textTotalLabel        || 'TOTAL:',
      progressPartial:   root.dataset.textProgressPartial   || 'Tu pack tiene {n} de {size} guantes',
      progressComplete:  root.dataset.textProgressComplete  || '¡Pack completo!',
      finalize:          root.dataset.textFinalize          || 'FINALIZAR COMPRA',
      missingSingular:   root.dataset.textMissingSingular   || 'FALTA 1 GUANTE',
      missingPlural:     root.dataset.textMissingPlural     || 'FALTAN {n} GUANTES',
      cartView:          root.dataset.textCartView          || 'VER CARRITO',
      cartHide:          root.dataset.textCartHide          || 'OCULTAR',
      cardAdded:         root.dataset.textCardAdded         || 'AÑADIDO',
      cardFull:          root.dataset.textCardFull          || 'PACK COMPLETO',
      cardSoldOut:       root.dataset.textCardSoldOut       || 'AGOTADO',
    };
    this._t = (key, vars) => {
      let s = this.texts[key] || '';
      if (vars) for (const k in vars) s = s.split('{' + k + '}').join(vars[k]);
      return s;
    };

    this.packSize = parseInt(root.dataset.packSize || '3', 10);
    this.items = []; // { variantId, productId, productHandle, productTitle, productUrl, sizeLabel, price, imgSrc }
    this.filters = { cut: new Set(), glove: new Set(), palm: new Set(), range: new Set() };

    // Personalization fee (single charge per pack regardless of how many gloves are personalized)
    this.persoVariantId = root.dataset.personalizationVariant || null;
    this.persoPrice = parseInt(root.dataset.personalizationPrice || '0', 10);
    this.root = root;

    this.cache = {
      shell: root.querySelector('.pp2__shell'),
      catalog: root.querySelector('.pp2__catalog'),
      grid: root.querySelector('[data-pp2-grid]'),
      noResults: root.querySelector('[data-pp2-no-results]'),
      chips: root.querySelector('[data-pp2-chips]'),
      cards: Array.from(root.querySelectorAll('[data-pp2-card]')),
      filters: Array.from(root.querySelectorAll('[data-pp2-filter]')),
      slots: root.querySelector('[data-pp2-slots]'),
      sizeBtns: Array.from(root.querySelectorAll('.pp2__size-btn')),
      savings: root.querySelector('[data-pp2-savings]'),
      savingsText: root.querySelector('[data-pp2-savings-text]'),
      totalLabel: root.querySelector('[data-pp2-total-label]'),
      totalAmount: root.querySelector('[data-pp2-total-amount]'),
      progress: root.querySelector('[data-pp2-progress]'),
      checkout: root.querySelector('[data-pp2-checkout]'),
      checkoutText: root.querySelector('[data-pp2-checkout-text]'),
      spinner: root.querySelector('.pp2__spinner'),
      announce: root.querySelector('[data-pp2-announce]'),
      toast: root.querySelector('[data-pp2-toast]'),
      personalizeBtn: root.querySelector('[data-pp2-personalize]'),
      personalizeModal: root.querySelector('[data-pp2-personalize-modal]'),
      personalizeList: root.querySelector('[data-pp2-personalize-list]'),
      personalizeEmpty: root.querySelector('[data-pp2-personalize-empty]'),
      personalizeCloseEls: Array.from(root.querySelectorAll('[data-pp2-personalize-close]')),
      personalizeSaveBtn: root.querySelector('[data-pp2-personalize-save]'),
      infoBtn: root.querySelector('[data-pp2-info-open]'),
      infoModal: root.querySelector('[data-pp2-info-modal]'),
      infoCloseEls: Array.from(root.querySelectorAll('[data-pp2-info-close]')),
      cartBar: root.querySelector('[data-pp2-cart-bar]'),
      cartProgress: root.querySelector('[data-pp2-cart-progress]'),
      cartExpanded: root.querySelector('[data-pp2-cart-expanded]'),
      cartList: root.querySelector('[data-pp2-cart-list]'),
      cartEmpty: root.querySelector('[data-pp2-cart-empty]'),
      cartCount: root.querySelector('[data-pp2-cart-count]'),
      cartTotal: root.querySelector('[data-pp2-cart-total]'),
      cartCloseEls: Array.from(root.querySelectorAll('[data-pp2-cart-close]')),
      cartToggle: root.querySelector('[data-pp2-cart-toggle]'),
      cartToggleLabel: root.querySelector('[data-pp2-cart-toggle-label]'),
      cartFinalize: root.querySelector('[data-pp2-cart-finalize]'),
      cartFinalizeLabel: root.querySelector('[data-pp2-cart-finalize-label]'),
      cartSpinner: root.querySelector('[data-pp2-cart-spinner]'),
      panel: root.querySelector('.pp2__panel'),
    };

    this._init();
  }

  _init() {
    this._restoreState();
    this._readUrlFilters();
    this._wireFilters();
    this._wireCards();
    this._wireSizeSelector();
    this._wireCheckout();
    this._wirePersonalize();
    this._wireCartBar();
    this._renderSlots();
    this._renderSizeSelector();
    this._renderChips();
    this._applyFilters();
    this._renderTotals();
    this._renderCartBar();
    this._toggleCartBar();
    window.addEventListener('resize', () => this._toggleCartBar());
  }

  /* ------------------------------------------------------- Filters */
  _wireFilters() {
    this.cache.filters.forEach((filterEl) => {
      const id = filterEl.dataset.filterId;
      const trigger = filterEl.querySelector('[data-pp2-filter-trigger]');
      const panel = filterEl.querySelector('[data-pp2-filter-panel]');
      const closeBtn = filterEl.querySelector('[data-pp2-filter-close]');
      const clearBtn = filterEl.querySelector('[data-pp2-filter-clear]');
      const inputs = Array.from(filterEl.querySelectorAll('[data-pp2-filter-input]'));

      trigger.addEventListener('click', () => {
        const isOpen = panel.hasAttribute('hidden') === false;
        if (isOpen) {
          this._closeAllFilters();
          return;
        }
        this._closeAllFilters();
        panel.removeAttribute('hidden');
        trigger.setAttribute('aria-expanded', 'true');
        const firstInput = panel.querySelector('input');
        if (firstInput) firstInput.focus();
      });

      closeBtn.addEventListener('click', () => {
        panel.setAttribute('hidden', '');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      });

      clearBtn.addEventListener('click', () => {
        inputs.forEach((i) => { i.checked = false; });
        this.filters[id].clear();
        this._afterFilterChange();
      });

      inputs.forEach((input) => {
        if (this.filters[id].has(input.value)) {
          input.checked = true;
        }
        input.addEventListener('change', () => {
          if (input.checked) this.filters[id].add(input.value);
          else this.filters[id].delete(input.value);
          this._afterFilterChange();
        });
      });
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-pp2-filter]')) {
        this._closeAllFilters();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._closeAllFilters();
    });

    // Clear-all button in no-results state
    const clearAll = this.root.querySelector('[data-pp2-clear-all]');
    if (clearAll) {
      clearAll.addEventListener('click', () => this._clearAllFilters());
    }
  }

  _closeAllFilters() {
    this.cache.filters.forEach((filterEl) => {
      const panel = filterEl.querySelector('[data-pp2-filter-panel]');
      const trigger = filterEl.querySelector('[data-pp2-filter-trigger]');
      panel.setAttribute('hidden', '');
      trigger.setAttribute('aria-expanded', 'false');
    });
  }

  _afterFilterChange() {
    this._renderFilterCounts();
    this._renderChips();
    this._applyFilters();
    this._writeUrlFilters();
  }

  _clearAllFilters() {
    Object.keys(this.filters).forEach((k) => this.filters[k].clear());
    this.cache.filters.forEach((filterEl) => {
      filterEl.querySelectorAll('[data-pp2-filter-input]').forEach((i) => { i.checked = false; });
    });
    this._afterFilterChange();
  }

  _renderFilterCounts() {
    this.cache.filters.forEach((filterEl) => {
      const id = filterEl.dataset.filterId;
      const count = this.filters[id].size;
      const countEl = filterEl.querySelector('[data-pp2-filter-count]');
      if (count > 0) {
        countEl.textContent = String(count);
        countEl.removeAttribute('hidden');
        filterEl.dataset.active = 'true';
      } else {
        countEl.setAttribute('hidden', '');
        filterEl.dataset.active = 'false';
      }
    });
  }

  _renderChips() {
    const chips = this.cache.chips;
    chips.innerHTML = '';
    let hasAny = false;
    Object.entries(this.filters).forEach(([id, set]) => {
      set.forEach((value) => {
        hasAny = true;
        const chip = document.createElement('span');
        chip.className = 'pp2-chip';
        chip.innerHTML = `
          <span>${this._escapeHtml(value)}</span>
          <button type="button" class="pp2-chip__remove" aria-label="Quitar filtro: ${this._escapeAttr(value)}">✕</button>
        `;
        chip.querySelector('button').addEventListener('click', () => {
          this.filters[id].delete(value);
          // Uncheck the corresponding input
          const filterEl = this.cache.filters.find((f) => f.dataset.filterId === id);
          if (filterEl) {
            const input = filterEl.querySelector(`[data-pp2-filter-input][value="${CSS.escape(value)}"]`);
            if (input) input.checked = false;
          }
          this._afterFilterChange();
        });
        chips.appendChild(chip);
      });
    });
    if (hasAny) {
      const clearAll = document.createElement('span');
      clearAll.className = 'pp2-chip pp2-chip--clear';
      clearAll.innerHTML = `<span>Limpiar todo</span>
        <button type="button" class="pp2-chip__remove" aria-label="Quitar todos los filtros">✕</button>`;
      clearAll.querySelector('button').addEventListener('click', () => this._clearAllFilters());
      chips.appendChild(clearAll);
    }
  }

  _applyFilters() {
    const filterMap = {
      cut: 'cut', glove: 'glove', palm: 'palm', range: 'range'
    };
    let visibleCount = 0;
    this.cache.cards.forEach((card) => {
      let visible = true;
      for (const [filterId, attr] of Object.entries(filterMap)) {
        const set = this.filters[filterId];
        if (set.size === 0) continue;
        const cardVal = card.dataset[attr] || '';
        if (!set.has(cardVal)) {
          visible = false;
          break;
        }
      }
      if (visible) {
        card.hidden = false;
        visibleCount++;
      } else {
        card.hidden = true;
      }
    });
    this.cache.catalog.dataset.noResults = visibleCount === 0 && this.cache.cards.length > 0 ? 'true' : 'false';
  }

  _readUrlFilters() {
    const url = new URL(window.location.href);
    ['cut', 'glove', 'palm', 'range'].forEach((id) => {
      const v = url.searchParams.get(id);
      if (!v) return;
      v.split(',').forEach((val) => {
        const decoded = decodeURIComponent(val);
        if (decoded) this.filters[id].add(decoded);
      });
    });
  }

  _writeUrlFilters() {
    const url = new URL(window.location.href);
    Object.entries(this.filters).forEach(([id, set]) => {
      if (set.size > 0) {
        url.searchParams.set(id, Array.from(set).map(encodeURIComponent).join(','));
      } else {
        url.searchParams.delete(id);
      }
    });
    window.history.replaceState({}, '', url);
  }

  /* ------------------------------------------------------- Cards */
  _wireCards() {
    this.cache.cards.forEach((card) => {
      const addBtn = card.querySelector('[data-pp2-add]');
      const select = card.querySelector('[data-pp2-size-select]');
      const tip = card.querySelector('[data-pp2-size-tip]');

      select.addEventListener('change', () => {
        if (tip) tip.hidden = true;
        this._refreshCardState(card);
      });

      addBtn.addEventListener('click', () => {
        // Duplicates are allowed: same product + same size can be added more
        // than once (los slots se diferencian con su key única). El cliente
        // quita un guante desde el slot del pack, no desde la tarjeta.
        if (!select.value) {
          if (tip) {
            tip.hidden = false;
            setTimeout(() => { tip.hidden = true; }, 2400);
          }
          card.dataset.shake = 'true';
          setTimeout(() => { delete card.dataset.shake; }, 360);
          return;
        }
        if (this.items.length >= this.packSize) {
          this._showFullPackToast();
          return;
        }
        this._addToPack(card);
      });

      this._refreshCardState(card);
    });
  }

  _refreshCardState(card) {
    const addBtn = card.querySelector('[data-pp2-add]');
    const labelEl = card.querySelector('[data-pp2-add-label]');
    const select = card.querySelector('[data-pp2-size-select]');

    // Duplicates allowed: don't switch the card to "added" state when the
    // same variant is already in the pack. Only "full" matters (pack size
    // reached) — until then the card stays in default ADD state so the
    // client can add multiple identical gloves.
    if (this.items.length >= this.packSize) {
      addBtn.dataset.state = 'full';
      labelEl.textContent = this._t('cardFull');
      return;
    }
    addBtn.dataset.state = '';
    labelEl.textContent = addBtn.dataset.defaultLabel || 'AÑADIR';

    const allOutOfStock = Array.from(select.options)
      .filter((o) => o.value)
      .every((o) => o.disabled);
    if (allOutOfStock) {
      select.disabled = true;
      addBtn.dataset.state = 'full';
      labelEl.textContent = this._t('cardSoldOut');
    }
  }

  _refreshAllCardStates() {
    this.cache.cards.forEach((card) => this._refreshCardState(card));
  }

  _isCardInPack(card) {
    const key = this._getCardItemKey(card);
    return this.items.some((i) => i.key === key);
  }

  _getCardItemKey(card) {
    const select = card.querySelector('[data-pp2-size-select]');
    const variantId = select.value;
    return `${card.dataset.productId}:${variantId}`;
  }

  _addToPack(card) {
    const select = card.querySelector('[data-pp2-size-select]');
    const variantOption = select.options[select.selectedIndex];
    const sizeLabel = variantOption.textContent.replace(/\s*\(Sin stock\)\s*$/, '').trim();
    const price = parseInt(variantOption.dataset.price, 10);
    const img = card.querySelector('[data-pp2-card-img]');

    // Unique slot key: productId:variantId is no longer unique because the
    // same product+size can appear twice. Suffix with timestamp+random so
    // _removeFromPack can target the exact slot.
    const slotSuffix = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const item = {
      key: `${card.dataset.productId}:${select.value}:${slotSuffix}`,
      variantId: select.value,
      productId: card.dataset.productId,
      productHandle: card.dataset.productHandle,
      productTitle: card.dataset.productTitle,
      productUrl: card.dataset.productUrl,
      sizeLabel,
      price,
      imgSrc: img ? img.currentSrc || img.src : '',
    };
    this.items.push(item);
    this._announce(`${card.dataset.productTitle} añadido al pack. Pack: ${this.items.length} de ${this.packSize} guantes.`);
    this._renderSlots();
    this._renderTotals();
    this._refreshAllCardStates();
    this._saveState();
  }

  _removeFromPack(key) {
    const item = this.items.find((i) => i.key === key);
    this.items = this.items.filter((i) => i.key !== key);
    if (item) {
      this._announce(`${item.productTitle} retirado del pack. Pack: ${this.items.length} de ${this.packSize} guantes.`);
    }
    this._renderSlots();
    this._renderTotals();
    this._refreshAllCardStates();
    this._saveState();
  }

  /* ------------------------------------------------------- Pack size */
  _wireSizeSelector() {
    this.cache.sizeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const newSize = parseInt(btn.dataset.size, 10);
        this._changePackSize(newSize);
      });
    });
  }

  _renderSizeSelector() {
    this.cache.sizeBtns.forEach((btn) => {
      const size = parseInt(btn.dataset.size, 10);
      btn.setAttribute('aria-checked', size === this.packSize ? 'true' : 'false');
    });
  }

  _changePackSize(newSize) {
    if (!this.maxSizes.includes(newSize)) return;
    if (newSize < this.items.length) {
      const drop = this.items.length - newSize;
      const ok = window.confirm(
        `Vas a reducir tu pack a ${newSize} guantes. Se quitarán los últimos ${drop} guantes seleccionados. ¿Continuar?`
      );
      if (!ok) return;
      this.items = this.items.slice(0, newSize);
    }
    this.packSize = newSize;
    this._renderSizeSelector();
    this._renderSlots();
    this._renderTotals();
    this._refreshAllCardStates();
    this._saveState();
  }

  /* ------------------------------------------------------- Slots */
  _renderSlots() {
    const container = this.cache.slots;
    container.style.setProperty('--pp2-slot-count', this.packSize);
    container.dataset.count = String(this.packSize);
    container.innerHTML = '';
    for (let i = 0; i < this.packSize; i++) {
      const item = this.items[i];
      const slot = document.createElement('div');
      slot.className = 'pp2-slot';
      slot.setAttribute('role', 'listitem');
      slot.dataset.index = String(i);
      if (item) {
        slot.classList.add('pp2-slot--filled');
        slot.innerHTML = `
          <img class="pp2-slot__img" src="${this._escapeAttr(item.imgSrc)}" alt="${this._escapeAttr(item.productTitle)}" loading="lazy">
          <button type="button" class="pp2-slot__remove" aria-label="Quitar ${this._escapeAttr(item.productTitle)} del pack, posición ${i + 1}">✕</button>
        `;
        slot.title = `${item.productTitle} · Talla ${item.sizeLabel}`;
        slot.querySelector('button').addEventListener('click', () => this._removeFromPack(item.key));
      } else {
        slot.classList.add('pp2-slot--empty');
        slot.innerHTML = `
          <button type="button" class="pp2-slot__cta" aria-label="Slot vacío ${i + 1}. Pulsa para elegir un guante."></button>
        `;
        slot.querySelector('button').addEventListener('click', () => {
          this.cache.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      container.appendChild(slot);
    }
  }

  _hasAnyPersonalization() {
    return this.items.some((item) => {
      const p = item.personalization;
      if (!p) return false;
      const r = (p.right || '').trim();
      const l = (p.left || '').trim();
      return Boolean(r || l);
    });
  }

  _personalizationFeeCents() {
    return (this.persoVariantId && this._hasAnyPersonalization()) ? this.persoPrice : 0;
  }

  /* ------------------------------------------------------- Promo math */
  _computePromoDiscount() {
    if (!this.items.length) return 0;
    const prices = this.items.map((i) => i.price).slice().sort((a, b) => a - b);
    const qty = Math.min(this.promoDiscountedQty || 1, prices.length);
    let sumCheapest = 0;
    for (let i = 0; i < qty; i++) sumCheapest += prices[i];
    return Math.round((sumCheapest * (this.promoDiscountPct || 0)) / 100);
  }

  /* ------------------------------------------------------- Totals */
  _renderTotals() {
    const subtotal = this.items.reduce((sum, i) => sum + i.price, 0);
    const isComplete = this.items.length === this.packSize;
    const discountAmount = isComplete ? this._computePromoDiscount() : 0;
    const persoFee = this._personalizationFeeCents();
    const total = subtotal - discountAmount + persoFee;

    this.cache.totalLabel.textContent = this._t('totalLabel');
    if (this.items.length === 0) {
      this.cache.totalAmount.textContent = '—€';
    } else {
      const amountStr = this._formatMoney(Math.round(total));
      this.cache.totalAmount.textContent = amountStr;
      this.cache.totalAmount.dataset.pulse = 'true';
      setTimeout(() => { delete this.cache.totalAmount.dataset.pulse; }, 320);
    }

    // Savings badge was removed from the markup (info already shown in the
    // promo banner). Guard against null so the JS continues to work.
    if (this.cache.savings && this.cache.savingsText) {
      this.cache.savings.hidden = false;
      this.cache.savings.classList.remove('pp2__savings--invisible');
      if (isComplete && discountAmount > 0) {
        this.cache.savingsText.textContent = `Ahorra ${this._formatMoney(Math.round(discountAmount))}`;
      } else if (this.items.length > 0 && this.items.length < this.packSize) {
        // Proyección dinámica del ahorro según el porcentaje configurado.
        // pct = 100 → el más barato sale gratis, así que la proyección es
        // su precio completo. pct < 100 → fracción correspondiente.
        const projectedCheapest = Math.min(...this.items.map(i => i.price));
        const projectedSavings = Math.round(projectedCheapest * (this.promoDiscountPct || 0) / 100);
        this.cache.savingsText.textContent = `Ahorra hasta ${this._formatMoney(projectedSavings)}`;
      } else {
        this.cache.savingsText.textContent = this.promoDiscountPct >= 100
          ? `El más barato GRATIS`
          : `−${this.promoDiscountPct}% sobre el más barato`;
      }
    }

    if (this.items.length === 0) {
      this.cache.progress.textContent = '';
    } else if (this.items.length < this.packSize) {
      this.cache.progress.textContent = this._t('progressPartial', { n: this.items.length, size: this.packSize });
    } else {
      this.cache.progress.textContent = this._t('progressComplete');
    }

    const ready = this.items.length >= this.packSize;
    this.cache.checkout.disabled = !ready;
    if (ready) {
      this.cache.checkoutText.textContent = this._t('finalize');
    } else {
      const missing = this.packSize - this.items.length;
      this.cache.checkoutText.textContent = missing === 1 ? this._t('missingSingular') : this._t('missingPlural', { n: missing });
    }

    // Mirror state into the mobile cart bar
    this._renderCartBar();
  }

  /* ------------------------------------------------------- Checkout */
  _wireCheckout() {
    this.cache.checkout.addEventListener('click', async () => {
      if (this.cache.checkout.disabled) return;
      try {
        this._setCheckoutLoading(true);
        await this._submitToCart();
        this._clearStateAfterCheckout();
        window.location.href = '/cart';
      } catch (err) {
        console.error('[crea-tu-pack] checkout error', err);
        this._toast(err.message || 'No se pudo añadir al carrito', 'error');
        this._setCheckoutLoading(false);
      }
    });
  }

  _setCheckoutLoading(loading) {
    if (this.cache.checkout) this.cache.checkout.disabled = loading;
    if (this.cache.spinner) this.cache.spinner.hidden = !loading;
  }

  async _submitToCart() {
    const items = this.items.map((item, idx) => {
      // Solo properties visibles y de uso real en el pedido. Antes había
      // _pack_id / _pack_size / _pack_position con prefijo "_" para ocultarse
      // en el carrito, pero Shopify admin SÍ los muestra al merchant, así
      // que añadían ruido al detalle del pedido sin valor operativo.
      const properties = {
        Pack: `Pack de ${this.packSize} guantes`,
      };
      const p = item.personalization;
      if (p) {
        const right = (p.right || '').trim();
        const left = (p.left || '').trim();
        // Orden requerido: IZQUIERDO primero, DERECHO segundo. Solo se
        // añaden si tienen texto (no líneas vacías en el pedido admin).
        if (left)  properties['Personalización guante izquierdo'] = left;
        if (right) properties['Personalización guante derecho']  = right;
      }
      return {
        id: parseInt(item.variantId, 10),
        quantity: 1,
        properties,
      };
    });

    // Append the personalization fee once if any glove carries personalization
    if (this.persoVariantId && this._hasAnyPersonalization()) {
      items.push({
        id: parseInt(this.persoVariantId, 10),
        quantity: 1,
        properties: {
          Pack: `Pack de ${this.packSize} guantes`,
        },
      });
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ items }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      console.error('[crea-tu-pack] /cart/add.js failed', { status: res.status, body: data, items });
      const msg = data.description || data.message || (data.errors && JSON.stringify(data.errors)) || `Error ${res.status} al añadir al carrito`;
      throw new Error(msg);
    }
    return res.json();
  }

  _clearStateAfterCheckout() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  /* ------------------------------------------------------- Modals (info + personalize) */
  _wirePersonalize() {
    // Personalize modal trigger
    if (this.cache.personalizeBtn && this.cache.personalizeModal) {
      this.cache.personalizeBtn.addEventListener('click', () => {
        this._openPersonalizeModal();
      });
      this.cache.personalizeCloseEls.forEach((el) => {
        el.addEventListener('click', () => this._closePersonalizeModal(false));
      });
      if (this.cache.personalizeSaveBtn) {
        this.cache.personalizeSaveBtn.addEventListener('click', () => {
          this._savePersonalization();
        });
      }
    }

    // Info modal trigger
    if (this.cache.infoBtn && this.cache.infoModal) {
      this.cache.infoBtn.addEventListener('click', () => {
        this._openInfoModal();
      });
      this.cache.infoCloseEls.forEach((el) => {
        el.addEventListener('click', () => this._closeInfoModal());
      });
    }

    // Global Escape closes whichever modal is open
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (this.cache.personalizeModal && !this.cache.personalizeModal.hidden) {
        this._closePersonalizeModal(false);
      } else if (this.cache.infoModal && !this.cache.infoModal.hidden) {
        this._closeInfoModal();
      }
    });
  }

  _openPersonalizeModal() {
    this._renderPersonalizeList();
    this.cache.personalizeModal.hidden = false;
    this._lockBodyScroll();
    this.root.dataset.modalOpen = 'true';
    // Focus first input or close button
    const firstInput = this.cache.personalizeList.querySelector('input');
    if (firstInput) {
      firstInput.focus();
    } else if (this.cache.personalizeCloseEls[0]) {
      this.cache.personalizeCloseEls[0].focus();
    }
  }

  _closePersonalizeModal(_saved) {
    this.cache.personalizeModal.hidden = true;
    this._unlockBodyScroll();
    delete this.root.dataset.modalOpen;
    if (this.cache.personalizeBtn) this.cache.personalizeBtn.focus();
  }

  /* ------------------------------------------------------- Scroll lock */
  // No-op: setting overflow: hidden on body breaks `position: sticky` of
  // the panel (sticky's scroll container becomes the body, which then has
  // no scroll, so sticky degenerates to relative and the panel jumps
  // out of view). Modal already overlays via position: fixed.
  _lockBodyScroll() { /* intentionally empty */ }
  _unlockBodyScroll() { /* intentionally empty */ }

  _renderPersonalizeList() {
    const list = this.cache.personalizeList;
    const empty = this.cache.personalizeEmpty;
    const saveBtn = this.cache.personalizeSaveBtn;
    list.innerHTML = '';

    if (this.items.length === 0) {
      empty.hidden = false;
      if (saveBtn) saveBtn.disabled = true;
      return;
    }
    empty.hidden = true;
    if (saveBtn) saveBtn.disabled = false;

    this.items.forEach((item, idx) => {
      const li = document.createElement('li');
      li.className = 'pp2__personalize-item';
      const personalization = item.personalization || { right: '', left: '' };
      const safeImg = this._escapeAttr(item.imgSrc);
      const safeTitle = this._escapeHtml(item.productTitle);
      const safeSize = this._escapeHtml(item.sizeLabel);
      const safeRight = this._escapeAttr(personalization.right || '');
      const safeLeft = this._escapeAttr(personalization.left || '');
      const idRight = `pp2-pers-${idx}-right`;
      const idLeft = `pp2-pers-${idx}-left`;
      li.innerHTML = `
        <img class="pp2__personalize-thumb" src="${safeImg}" alt="${safeTitle}" loading="lazy">
        <div class="pp2__personalize-info">
          <span class="pp2__personalize-product">
            ${safeTitle}
            <small>Talla ${safeSize}</small>
          </span>
          <div class="pp2__personalize-fields">
            <div class="pp2__personalize-field">
              <label class="pp2__personalize-label" for="${idLeft}">Guante izquierdo</label>
              <input
                id="${idLeft}"
                type="text"
                class="pp2__personalize-input"
                data-side="left"
                data-item-key="${this._escapeAttr(item.key)}"
                maxlength="20"
                placeholder="Ej: NOMBRE"
                value="${safeLeft}"
              >
            </div>
            <div class="pp2__personalize-field">
              <label class="pp2__personalize-label" for="${idRight}">Guante derecho</label>
              <input
                id="${idRight}"
                type="text"
                class="pp2__personalize-input"
                data-side="right"
                data-item-key="${this._escapeAttr(item.key)}"
                maxlength="20"
                placeholder="Ej: APELLIDO"
                value="${safeRight}"
              >
            </div>
          </div>
        </div>
      `;
      list.appendChild(li);
      // Sanitiza cada input en vivo + muestra aviso si se filtra algo.
      const STRIP = /[^\p{L}\p{N} .\-']/gu;
      const WARN_MSG = 'No se permiten emojis ni caracteres especiales.';
      const showWarn = (inp) => {
        let group = inp.closest('.pp2__personalize-field') || inp.parentNode;
        if (!group) return;
        let w = group.querySelector('.pp2__personalize-warn');
        if (!w) {
          w = document.createElement('div');
          w.className = 'pp2__personalize-warn';
          w.setAttribute('role', 'alert');
          w.style.cssText = 'color:#c1121f;font-size:12px;margin-top:4px;line-height:1.35;';
          w.textContent = WARN_MSG;
          group.appendChild(w);
        }
        w.style.display = '';
        clearTimeout(w._t);
        w._t = setTimeout(() => { w.style.display = 'none'; }, 2500);
      };
      li.querySelectorAll('input.pp2__personalize-input').forEach((inp) => {
        inp.addEventListener('input', () => {
          const cleaned = String(inp.value).replace(STRIP, '');
          if (cleaned !== inp.value) {
            const pos = inp.selectionStart;
            inp.value = cleaned;
            try { inp.setSelectionRange(pos, pos); } catch (e) {}
            showWarn(inp);
          }
        });
        inp.addEventListener('paste', (e) => {
          try {
            const d = e.clipboardData || window.clipboardData;
            if (!d) return;
            const t = d.getData('text');
            if (t && t.replace(STRIP, '') !== t) showWarn(inp);
          } catch (er) {}
        });
      });
    });
  }

  _savePersonalization() {
    const inputs = Array.from(this.cache.personalizeList.querySelectorAll('input[data-item-key]'));
    let touched = 0;
    inputs.forEach((input) => {
      const key = input.dataset.itemKey;
      const side = input.dataset.side;
      const item = this.items.find((i) => i.key === key);
      if (!item) return;
      if (!item.personalization) item.personalization = { right: '', left: '' };
      // Cinturón final anti-emoji antes de guardar.
      const value = String(input.value || '').replace(/[^\p{L}\p{N} .\-']/gu, '').trim();
      if (item.personalization[side] !== value) touched++;
      item.personalization[side] = value;
    });
    this._saveState();
    this._closePersonalizeModal(true);
    // Refresh totals so the personalization fee (or its removal) shows up
    this._renderTotals();
    this._renderCartBar();
    if (touched > 0) {
      this._toast('Personalización guardada.', 'success');
    }
  }

  _openInfoModal() {
    this.cache.infoModal.hidden = false;
    this._lockBodyScroll();
    this.root.dataset.modalOpen = 'true';
    if (this.cache.infoCloseEls[0]) this.cache.infoCloseEls[0].focus();
  }

  _closeInfoModal() {
    this.cache.infoModal.hidden = true;
    this._unlockBodyScroll();
    delete this.root.dataset.modalOpen;
    if (this.cache.infoBtn) this.cache.infoBtn.focus();
  }

  /* ------------------------------------------------------- Mobile cart bar */
  _wireCartBar() {
    if (!this.cache.cartBar) return;

    if (this.cache.cartToggle) {
      this.cache.cartToggle.addEventListener('click', () => this._toggleCartExpanded());
    }
    this.cache.cartCloseEls.forEach((el) => {
      el.addEventListener('click', () => this._setCartExpanded(false));
    });
    if (this.cache.cartFinalize) {
      this.cache.cartFinalize.addEventListener('click', async () => {
        if (this.cache.cartFinalize.disabled) return;
        try {
          this._setFinalizeLoading(true);
          await this._submitToCart();
          this._clearStateAfterCheckout();
          window.location.href = '/cart';
        } catch (err) {
          console.error('[crea-tu-pack] checkout error', err);
          this._toast(err.message || 'No se pudo añadir al carrito', 'error');
          this._setFinalizeLoading(false);
        }
      });
    }
  }

  _toggleCartBar() {
    if (!this.cache.cartBar) return;
    // Promo 2x: sticky cart is visible on BOTH mobile and desktop.
    this.cache.cartBar.hidden = false;
  }

  _toggleCartExpanded() {
    const isOpen = this.cache.cartBar.dataset.expanded === 'true';
    this._setCartExpanded(!isOpen);
  }

  _setCartExpanded(open) {
    const exp = this.cache.cartExpanded;
    if (!exp) return;
    if (open) {
      this.cache.cartBar.dataset.expanded = 'true';
      exp.hidden = false;
      exp.setAttribute('aria-hidden', 'false');
      if (this.cache.cartToggle) this.cache.cartToggle.setAttribute('aria-expanded', 'true');
      if (this.cache.cartToggleLabel) this.cache.cartToggleLabel.textContent = this._t('cartHide');
      document.body.dataset.ctpMobile = 'open';
    } else {
      this.cache.cartBar.dataset.expanded = 'false';
      exp.hidden = true;
      exp.setAttribute('aria-hidden', 'true');
      if (this.cache.cartToggle) this.cache.cartToggle.setAttribute('aria-expanded', 'false');
      if (this.cache.cartToggleLabel) this.cache.cartToggleLabel.textContent = this._t('cartView');
      document.body.dataset.ctpMobile = 'closed';
    }
  }

  _renderCartBar() {
    if (!this.cache.cartBar) return;

    if (this.cache.cartCount) {
      this.cache.cartCount.textContent = `${this.items.length}/${this.packSize}`;
    }

    this._renderCartProgress();

    const subtotal = this.items.reduce((s, i) => s + i.price, 0);
    const isComplete = this.items.length === this.packSize;
    const discountAmount = isComplete ? this._computePromoDiscount() : 0;
    const persoFee = this._personalizationFeeCents();
    const total = subtotal - discountAmount + persoFee;

    if (this.cache.cartTotal) {
      this.cache.cartTotal.textContent = this.items.length === 0
        ? '—€'
        : this._formatMoney(Math.round(total));
    }

    // Disable finalize until the pack is fully completed
    if (this.cache.cartFinalize) {
      const canCheckout = this.items.length >= this.packSize;
      this.cache.cartFinalize.disabled = !canCheckout;
      if (this.cache.cartFinalizeLabel) {
        if (canCheckout) {
          this.cache.cartFinalizeLabel.textContent = this._t('finalize');
        } else {
          const missing = Math.max(0, this.packSize - this.items.length);
          this.cache.cartFinalizeLabel.textContent = missing === 1 ? this._t('missingSingular') : this._t('missingPlural', { n: missing });
        }
      }
    }

    // Render list
    const list = this.cache.cartList;
    const empty = this.cache.cartEmpty;
    if (!list || !empty) return;

    list.innerHTML = '';
    if (this.items.length === 0) {
      empty.hidden = false;
      list.hidden = true;
      return;
    }
    empty.hidden = true;
    list.hidden = false;

    // Identify which slots receive the promo discount: the N cheapest items
    // (N = promoDiscountedQty). Only mark them when the pack is complete.
    const discountedKeys = new Set();
    if (isComplete && this.promoDiscountPct > 0) {
      const qty = Math.min(this.promoDiscountedQty || 1, this.items.length);
      const sorted = this.items.slice().sort((a, b) => a.price - b.price);
      for (let i = 0; i < qty; i++) discountedKeys.add(sorted[i].key);
    }
    const pct = this.promoDiscountPct || 0;

    this.items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'pp2-cart-item';

      const isDiscounted = discountedKeys.has(item.key);
      let priceHtml;
      if (isDiscounted) {
        if (pct >= 100) {
          priceHtml = `
            <span class="pp2-cart-item__price pp2-cart-item__price--free">
              <span class="pp2-cart-item__price-strike">${this._formatMoney(item.price)}</span>
              <span class="pp2-cart-item__price-free-tag">GRATIS</span>
            </span>`;
        } else {
          const discounted = Math.round(item.price * (100 - pct) / 100);
          priceHtml = `
            <span class="pp2-cart-item__price pp2-cart-item__price--discount">
              <span class="pp2-cart-item__price-strike">${this._formatMoney(item.price)}</span>
              <span class="pp2-cart-item__price-final">${this._formatMoney(discounted)}</span>
            </span>`;
        }
      } else {
        priceHtml = `<span class="pp2-cart-item__price">${this._formatMoney(item.price)}</span>`;
      }

      li.innerHTML = `
        <img class="pp2-cart-item__thumb" src="${this._escapeAttr(item.imgSrc)}" alt="${this._escapeAttr(item.productTitle)}" loading="lazy">
        <div class="pp2-cart-item__info">
          <span class="pp2-cart-item__name">${this._escapeHtml(item.productTitle)}</span>
          <span class="pp2-cart-item__size">Talla ${this._escapeHtml(item.sizeLabel)}</span>
        </div>
        ${priceHtml}
        <button type="button" class="pp2-cart-item__remove" aria-label="Quitar ${this._escapeAttr(item.productTitle)} del pack">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      `;
      li.querySelector('.pp2-cart-item__remove').addEventListener('click', () => {
        this._removeFromPack(item.key);
      });
      list.appendChild(li);
    });
  }

  _renderCartProgress() {
    const el = this.cache.cartProgress;
    if (!el) return;
    const filled = this.items.length;
    const size = this.packSize;
    if (el.children.length !== size) {
      el.innerHTML = '';
      for (let i = 0; i < size; i += 1) {
        const cell = document.createElement('span');
        cell.className = 'pp2__cart-progress-cell';
        el.appendChild(cell);
      }
    }
    Array.from(el.children).forEach((cell, i) => {
      cell.dataset.state = i < filled ? 'filled' : 'empty';
    });
  }

  _setFinalizeLoading(loading) {
    if (!this.cache.cartFinalize) return;
    if (loading) {
      this.cache.cartFinalize.disabled = true;
      if (this.cache.cartSpinner) this.cache.cartSpinner.hidden = false;
    } else {
      this.cache.cartFinalize.disabled = this.items.length < this.packSize;
      if (this.cache.cartSpinner) this.cache.cartSpinner.hidden = true;
    }
  }

  /* ------------------------------------------------------- State */
  _saveState() {
    try {
      const state = {
        pack_size: this.packSize,
        items: this.items.map((i) => ({
          variantId: i.variantId,
          productId: i.productId,
          productHandle: i.productHandle,
          productTitle: i.productTitle,
          productUrl: i.productUrl,
          sizeLabel: i.sizeLabel,
          price: i.price,
          imgSrc: i.imgSrc,
          key: i.key,
          personalization: i.personalization || null,
        })),
        updated_at: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // ignore (private mode, full quota, etc.)
    }
  }

  _restoreState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw);
      if (!state || !state.updated_at) return;
      if (Date.now() - state.updated_at > STORAGE_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      if ([2, 3, 4].includes(state.pack_size)) {
        this.packSize = state.pack_size;
        this.root.dataset.packSize = String(state.pack_size);
      }
      if (Array.isArray(state.items)) {
        this.items = state.items.filter((i) => i && i.variantId && i.productId);
      }
    } catch (e) {
      // ignore
    }
  }

  /* ------------------------------------------------------- Helpers */
  _showFullPackToast() {
    // Promo 2x: pack size is fixed at 2, no auto-grow path
    this._toast('Tu pack ya está completo. Quita un guante para añadir otro.', 'warning');
    return;
    // (legacy auto-grow path below, never executed in promo-2x)
    /* eslint-disable */
    const next = this.packSize + 1;
    if (next > 4) {
      this._toast('Tu pack ya está completo.', 'warning');
      return;
    }
    const toast = this.cache.toast;
    toast.hidden = false;
    toast.dataset.variant = 'warning';
    toast.innerHTML = `
      <span>Aumenta tu pack a ${next} guantes para añadir más.</span>
      <button type="button" style="margin-left:12px;background:#fff;color:#000;border:none;padding:4px 10px;border-radius:999px;cursor:pointer;font-weight:700;">Pack ${next}</button>
    `;
    toast.querySelector('button').addEventListener('click', () => {
      this._changePackSize(next);
      toast.hidden = true;
    });
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { toast.hidden = true; }, 5000);
  }

  _toast(message, variant = '') {
    const toast = this.cache.toast;
    if (!toast) return;
    toast.textContent = message;
    toast.dataset.variant = variant;
    toast.hidden = false;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => { toast.hidden = true; }, 4000);
  }

  _announce(msg) {
    if (!this.cache.announce) return;
    this.cache.announce.textContent = msg;
  }

  _formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return `${value}€`;
  }

  _uuid() {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  _escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }
  _escapeAttr(s) {
    return this._escapeHtml(s);
  }
}

function bootstrap() {
  document.querySelectorAll('.pp2[data-section-id]').forEach((root) => {
    if (root.dataset.ctpInitialized === 'true') return;
    root.dataset.ctpInitialized = 'true';
    new CreaTuPack(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

// Re-init on theme editor section load
document.addEventListener('shopify:section:load', bootstrap);
